import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "@mavenmm/teamwork-auth";

// Types matching the GraphQL schema
export interface Team {
  id: string;
  teamName: string;
}

export interface SubPod {
  id: string;
  name: string;
  is_deleted: boolean;
  pod_id: string;
}

export interface Pod {
  id: string;
  name: string;
  is_deleted: boolean;
  sub_pods: SubPod[];
}

// Flattened option for the dropdown: "Pod - SubPod"
export interface PodSubPodOption {
  id: string; // subPod id (or pod id if no subpods)
  label: string; // "Pod Name - SubPod Name" or just "Pod Name"
  podId: string;
  podName: string;
  subPodId: string | null;
  subPodName: string | null;
}

// Queries from: maven-dashboard/ui/src/nav-groups/Admin/Forecasting/Admin.graphql
const TEAMS_QUERY = `
  query AdminGetAllTeams {
    teams {
      id
      teamName
      order
    }
  }
`;

const PODS_QUERY = `
  query AdminPagePods {
    pods {
      id
      name
      senior_brand_maven_lead
      is_deleted
      sub_pods {
        id
        name
        is_deleted
        pod_id
        members {
          id
          first_name
          last_name
        }
      }
    }
  }
`;

async function fetchGraphQL<T>(token: string, query: string): Promise<T> {
  const response = await fetch("/.netlify/functions/graphql-proxy", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status}`);
  }

  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message);
  }

  return json.data as T;
}

export function useTeamsAndPods() {
  const { getAccessToken, isAuthenticated } = useAuthContext();

  const teamsQuery = useQuery({
    queryKey: ["teams"],
    queryFn: async () => {
      const token = getAccessToken();
      if (!token) throw new Error("No access token");
      const data = await fetchGraphQL<{ teams: Team[] }>(token, TEAMS_QUERY);
      return data.teams || [];
    },
    enabled: isAuthenticated,
    staleTime: 60 * 60 * 1000, // 1 hour - teams rarely change
  });

  const podsQuery = useQuery({
    queryKey: ["pods"],
    queryFn: async () => {
      const token = getAccessToken();
      if (!token) throw new Error("No access token");
      const data = await fetchGraphQL<{ pods: Pod[] }>(token, PODS_QUERY);
      return (data.pods || []).filter((p) => !p.is_deleted);
    },
    enabled: isAuthenticated,
    staleTime: 60 * 60 * 1000, // 1 hour - pods rarely change
  });

  // Build dropdown options from pods data
  const podOptions = useMemo(() => {
    if (!podsQuery.data) return [];

    const options: PodSubPodOption[] = [];
    for (const pod of podsQuery.data) {
      const activeSubPods = (pod.sub_pods || []).filter((sp) => !sp.is_deleted);

      if (activeSubPods.length > 0) {
        for (const subPod of activeSubPods) {
          options.push({
            id: subPod.id,
            label: `${pod.name} - ${subPod.name}`,
            podId: pod.id,
            podName: pod.name,
            subPodId: subPod.id,
            subPodName: subPod.name,
          });
        }
      } else {
        options.push({
          id: pod.id,
          label: pod.name,
          podId: pod.id,
          podName: pod.name,
          subPodId: null,
          subPodName: null,
        });
      }
    }
    return options;
  }, [podsQuery.data]);

  const refetch = () => {
    teamsQuery.refetch();
    podsQuery.refetch();
  };

  return {
    teams: teamsQuery.data || [],
    pods: podsQuery.data || [],
    podOptions,
    isLoading: teamsQuery.isLoading || podsQuery.isLoading,
    errors: teamsQuery.error || podsQuery.error
      ? [teamsQuery.error?.message, podsQuery.error?.message].filter(Boolean) as string[]
      : null,
    refetch,
  };
}
